import { codeSplit, Controller, FetchOptions, FetchPageOptions, AnalyticsStore, PageStore } from "bernie-core";
import { PageData } from "bernie-http";
import { BlossomCodeSplitterFactory } from "components/flexFramework/CodeSplitter/BlossomCodeSplitterFactory";
import { StaticMapStore } from "src/stores/staticMap/StaticMapStore";
import { CompositionStore } from "stores/CompositionStore";
import { CookiesStore } from "stores/CookiesStore";
import { DXStore } from "stores/DXStore";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import { ServerWizardStateStore } from "stores/wizard/ServerWizardStateStore";
import { Composition, ExtendedContextStore, ViewModelResponse } from "typings/flexFramework/FlexDefinitions";
import { ServerChatbotStore } from "src/stores/chatbotStore/ServerChatbotStore";
import { isVariantEnabled, Experiment } from "src/components/utility/experiment";

interface FlexFetchPageOptions extends FetchPageOptions {
  context: ExtendedContextStore;
}

interface WindowWithState extends Window {
  __STATE__: {
    compositionStore: CompositionStore;
  };
}

export class LandingController implements Controller {
  public pageId: string; // x-page-id is set in the middleware
  public path = "/*";
  public routeName = "flex-LandingPage";
  public bundles = ["flex-LandingPage"];
  public exact = false;
  public component = codeSplit(async () => {
    const routeComponent = import(/* webpackChunkName: "flex-LandingPage" */ "views/LandingPage") as any;
    let composition: Composition | undefined;

    // Client-side only
    if (typeof window !== "undefined") {
      const serializedState = ((window as unknown) as WindowWithState).__STATE__;
      composition =
        serializedState && serializedState.compositionStore ? serializedState.compositionStore.composition : undefined;
    }

    await BlossomCodeSplitterFactory.get({ composition }).codeSplitAllReactComponents();

    return routeComponent;
  });

  public async fetch(options: FetchOptions): Promise<any> {
    const { stores, request } = options;
    const flexViewModelStore = stores && stores.get<FlexViewModelStore>("flexViewModel");
    const pageStore = stores && stores.get<PageStore>("page");
    const contextStore = stores && stores.get<ExtendedContextStore>("context");
    const cookiesStore = stores && stores.get<CookiesStore>("cookies");
    const mapStore = stores && stores.get<StaticMapStore>("staticMap");
    const compositionStore = stores && stores.get<CompositionStore>("compositionStore");
    const wizardState = stores && stores.get<ServerWizardStateStore>("wizardState");
    const dxStore = stores && stores.get<DXStore>("dx");
    const analyticsStore = stores && stores.get<AnalyticsStore>("analytics");
    const chatbotStore = stores && stores.get<ServerChatbotStore>("chatbot");
    //  Get cookie string for wizard
    cookiesStore.cookie = (request && request.headers && request.headers.cookie) || "";
    if (wizardState && wizardState.setLastSearchState) {
      wizardState.setLastSearchState(cookiesStore.getCookie("lastSearchCookie")!, contextStore);
    }

    // We get it from the middleware which hijacks the contextStore
    const viewModelResponse = contextStore.viewModelResponse as ViewModelResponse;

    const footer: string = contextStore.footer;
    delete contextStore.footer;
    delete contextStore.viewModelResponse;

    flexViewModelStore.setupFlexViewModelStore(viewModelResponse, pageStore, contextStore, analyticsStore, footer);

    compositionStore.setupCompositionStore(viewModelResponse, contextStore, mapStore);

    dxStore.setupInitialJumplinks(viewModelResponse);

    const chatbotExperimentOn = isVariantEnabled(contextStore, Experiment.Chatbot_FAB_for_PWA);
    chatbotStore.chatbotExperimentOn = chatbotExperimentOn; //set in store for later use
    await chatbotStore.getTokenFromAuthorizer(request);

    return Promise.resolve({});
  }

  public fetchPageData(options: FlexFetchPageOptions): Promise<PageData> {
    const viewModelResponse = options.context.viewModelResponse as ViewModelResponse;
    const additionalScripts = BlossomCodeSplitterFactory.get({
      composition: viewModelResponse.composition
    })
      .getWebpackReactComponents()
      .map(component => component && component.webpackChunkName);

    const seoMetaData = import(/* webpackChunkName: "SeoUtils" */ "utils/seo/SeoUtils").then(({ default: _ }) => {
      return (
        _.buildPageDataWithSeoMetaDataFromViewModel(viewModelResponse) || {
          title: viewModelResponse.composition.title
        }
      );
    });

    // Basically giving a list of webpackChunkNames that we want
    return Promise.resolve(seoMetaData).then(m => {
      return {
        ...m,
        assetsToIncludeFilter: (assetKey: string) => additionalScripts.includes(assetKey),
        pageId: options.context.pageId
      };
    });
  }
}
